import { formatDistanceToNow } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

/**
 * Detect the user's browser timezone
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Formats a timestamp for display in the user's local timezone
 * Handles various timestamp formats and ensures consistent display
 */
export const formatTimeAgo = (timestamp: string): string => {
  if (!timestamp) {
    return 'Just now';
  }

  try {
    console.log('Original timestamp:', timestamp);
    
    // Ensure the timestamp has a 'Z' marker if it's an ISO format without timezone info
    let normalizedTimestamp = timestamp;
    if (timestamp.includes('T') && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-')) {
      normalizedTimestamp = `${timestamp}Z`;
      console.log('Normalized timestamp to UTC:', normalizedTimestamp);
    }
    
    // Parse the timestamp as UTC
    const dateUTC = new Date(normalizedTimestamp);
    if (isNaN(dateUTC.getTime())) {
      console.warn('Invalid date:', timestamp);
      return 'Just now';
    }
    
    console.log('UTC parsed date:', dateUTC.toISOString());
    
    // Get user's timezone
    const timeZone = getUserTimezone();
    console.log('User timezone:', timeZone);
    
    // Convert UTC date to user's local timezone
    const zonedDate = toZonedTime(dateUTC, timeZone);
    console.log('After timezone conversion:', 
      'UTC:', dateUTC.toISOString(),
      'Local:', zonedDate.toLocaleString(),
      'Hours diff:', (zonedDate.getTime() - dateUTC.getTime()) / 3600000
    );

    // Get current time
    const now = new Date();
    const diff = now.getTime() - zonedDate.getTime();
    console.log('Time difference (ms):', diff, 'Minutes:', Math.floor(diff / 60000));

    // Very recent or future dates show 'just now'
    if (diff < 60000 || zonedDate > now) {
      return 'just now';
    }

    // Format relative time in user's local timezone
    const result = formatDistanceToNow(zonedDate, { addSuffix: true });
    console.log('Final formatted time:', result);
    return result;
  } catch (error) {
    console.error('Error formatting timestamp:', error, timestamp);
    return 'Just now';
  }
};

/**
 * Ensures a timestamp has timezone information
 * If no timezone info exists, assumes UTC
 */
export const ensureTimezoneInfo = (timestamp: string): string => {
  if (!timestamp) return timestamp;
  
  // Only process string timestamps
  if (typeof timestamp !== 'string') return timestamp;
  
  // Already has timezone info
  if (timestamp.includes('Z') || timestamp.includes('+') || timestamp.includes('-')) {
    return timestamp;
  }
  
  // Add UTC marker if it's an ISO format
  if (timestamp.includes('T')) {
    return `${timestamp}Z`;
  }
  
  return timestamp;
}; 